import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, ExternalLink } from 'lucide-react';
import { cn } from '../../lib/utils';
import { HelpTerm } from '../../config/helpContent';

interface InfoTooltipProps {
  term?: HelpTerm;
  content?: string; // Conteúdo simples de texto
  children?: React.ReactNode;
  className?: string;
  iconSize?: 'sm' | 'md' | 'lg';
  position?: 'top' | 'bottom' | 'left' | 'right';
}

export function InfoTooltip({ 
  term, 
  content,
  children, 
  className,
  iconSize = 'sm',
  position = 'top',
}: InfoTooltipProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Se não tiver term nem content, não renderiza nada
  if (!term && !content) {
    return <>{children}</>;
  }

  const iconSizes = {
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-t-surface-700 border-x-transparent border-b-transparent',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-surface-700 border-x-transparent border-t-transparent',
    left: 'left-full top-1/2 -translate-y-1/2 border-l-surface-700 border-y-transparent border-r-transparent',
    right: 'right-full top-1/2 -translate-y-1/2 border-r-surface-700 border-y-transparent border-l-transparent',
  };

  return (
    <span 
      className={cn('relative inline-flex items-center gap-1', className)}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      {children}
      <button
        type="button"
        className="text-surface-400 hover:text-primary-400 transition-colors cursor-help"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
      >
        <HelpCircle className={iconSizes[iconSize]} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={cn(
              'absolute z-50 w-72 p-3 rounded-lg',
              'bg-surface-800 border border-surface-700 shadow-xl',
              positionClasses[position]
            )}
          >
            {/* Arrow */}
            <div className={cn(
              'absolute w-0 h-0 border-[6px]',
              arrowClasses[position]
            )} />

            {/* Content */}
            <div className="space-y-2">
              {term ? (
                <>
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="text-sm font-semibold text-primary-400">
                      {term.title}
                    </h4>
                    <span className="text-xs px-1.5 py-0.5 bg-surface-700 rounded text-surface-400 font-mono">
                      {term.term}
                    </span>
                  </div>
                  
                  <p className="text-xs text-surface-300 leading-relaxed">
                    {term.description}
                  </p>

                  {term.example && (
                    <div className="pt-2 border-t border-surface-700">
                      <p className="text-xs text-surface-400">
                        <span className="font-medium text-surface-300">Exemplo: </span>
                        {term.example}
                      </p>
                    </div>
                  )}

                  {term.learnMoreUrl && (
                    <a
                      href={term.learnMoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary-400 hover:text-primary-300 mt-2"
                    >
                      Saiba mais
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </>
              ) : (
                <p className="text-xs text-surface-300 leading-relaxed">
                  {content}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </span>
  );
}

export default InfoTooltip;
